import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Spin } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import Papa from 'papaparse';
import Swal from 'sweetalert2';
import { createLeavesBulk } from '../../service/bulkUploadService';
import * as XLSX from 'xlsx';

const BulkUploadModal = ({
    onUploadData,
    isVisible,
    handleCancel
}) => {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    const handleUpload = async () => {
        if (!file) {
            showWarning("Please select a file");
            return;
        }

        if (!data || data.length === 0) {
            showWarning("No valid data found in the file");
            return;
        }

        try {
            setLoading(true);
            console.log("Uploading data:", data);
            await createLeavesBulk(data);
            onUploadData();
            setLoading(false);
            document.getElementById('csvId').value = "";
            setFile(null);
            setData(null);
            handleCancel();
        } catch (error) {
            console.error("Error in uploading data:", error);
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFile(null);
        setData(null);
        document.getElementById('csvId').value = "";
    };

    const handleBulkUpload = async (event) => {
        try {
            const selectedFile = event.target.files[0];
            if (!selectedFile) return;

            setFile(selectedFile);

            if (selectedFile.name.endsWith('.csv')) {
                Papa.parse(selectedFile, {
                    header: true,
                    skipEmptyLines: true,
                    transformHeader: (header) => header.trim().replace(/^"|"$/g, ''),
                    transform: (value) => {
                        if (typeof value === 'string') {
                            return value.trim().replace(/^"|"$/g, '');
                        }
                        return value;
                    },
                    complete: (result) => {
                        console.log("Raw CSV result:", result);
                        const cleanedData = result.data
                            .map(row => ({
                                record_number: row['record_number'] || row['Record Number'],
                                leave_days: row['leave_days'] || row['Leave Days'],
                                epf_number: String(row['epf_number'] || row['EPF Number']).trim(),
                                leave_type: String(row['leave_type'] || row['Leave Type']).trim(),
                                appplied_on: row['appplied_on'] || row['Applied On'],
                                status: row['status'] || row['Status'],
                                reason: row['reason'] || row['Reason']
                            }))
                            .filter(row => row.record_number && row.epf_number);
                        
                        console.log("Parsed CSV Data:", cleanedData);
                        setData(cleanedData);
                    },
                    error: (error) => {
                        console.error("Error parsing CSV:", error);
                        showWarning("Error parsing CSV file.");
                    }
                });
            } else if (selectedFile.name.endsWith('.xlsx')) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    try {
                        const arrayBuffer = e.target.result;
                        const workbook = XLSX.read(arrayBuffer, { 
                            type: 'array',
                            cellDates: false,
                            cellNF: false,
                            cellText: false
                        });
                        const sheetName = workbook.SheetNames[0];
                        const sheet = workbook.Sheets[sheetName];
                        
                        const jsonData = XLSX.utils.sheet_to_json(sheet, { 
                            header: 1,
                            defval: '',
                            raw: true
                        });

                        console.log("Raw Excel data:", jsonData);

                        if (jsonData.length < 2) {
                            showWarning("Excel file must have at least a header row and one data row.");
                            return;
                        }

                        const headers = jsonData[0].map(h => String(h || '').trim());
                        console.log("Headers found:", headers);
                        
                        const dataRows = jsonData.slice(1);
                        
                        const cleanedData = dataRows
                            .map((row, index) => {
                                const rowData = {};
                                headers.forEach((header, headerIndex) => {
                                    const cellValue = row[headerIndex];
                                    rowData[header] = cellValue !== undefined ? cellValue : '';
                                });
                                
                                console.log(`Row ${index + 2}:`, rowData);
                                
                                return {
                                    record_number: rowData['record_number'] || rowData['Record Number'],
                                    leave_days: rowData['leave_days'] || rowData['Leave Days'],
                                    epf_number: String(rowData['epf_number'] || rowData['EPF Number'] || '').trim(),
                                    leave_type: String(rowData['leave_type'] || rowData['Leave Type'] || '').trim(),
                                    appplied_on: rowData['appplied_on'] || rowData['Applied On'],
                                    status: rowData['status'] || rowData['Status'],
                                    reason: rowData['reason'] || rowData['Reason']
                                };
                            })
                            .filter(row => {
                                const hasRequiredFields = row.record_number && row.epf_number;
                                if (!hasRequiredFields) {
                                    console.log("Filtered out row due to missing required fields:", row);
                                }
                                return hasRequiredFields;
                            });

                        console.log("Parsed Excel Data:", cleanedData);
                        
                        if (cleanedData.length === 0) {
                            showWarning("No valid data rows found in Excel file. Please check your data format.");
                            return;
                        }
                        
                        setData(cleanedData);
                    } catch (error) {
                        console.error("Error processing Excel file:", error);
                        showWarning("Error processing Excel file. Please check the file format.");
                    }
                };
                reader.onerror = (error) => {
                    console.error("Error reading Excel file:", error);
                    showWarning("Error reading Excel file.");
                };
                reader.readAsArrayBuffer(selectedFile);
            } else {
                showWarning("Unsupported file format. Please upload a CSV or XLSX file.");
            }
        } catch (error) {
            console.error("Error in file uploading:", error);
            showWarning("Error processing file.");
        }
    };

    const showWarning = (str) => {
        return Swal.fire({
            title: "Error",
            text: str,
            icon: "error",
        });
    };

    const downloadSampleFile = () => {
        const sampleData = [
            ["record_number", "leave_days", "epf_number", "leave_type", "appplied_on", "status", "reason"],
            ["1", "06/28/2025|FULL DAY,06/29/2025|FULL DAY", "846", "Special Leave", "15/06/2025", "Approved", "Sick"],
            ["2", "02/25/2025|SECOND HALF", "2332", "Short Leave", "16/05/2025", "Approved", "No Reason"]
        ];
        const csvString = Papa.unparse(sampleData);
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.setAttribute("download", "sample_leave_upload.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <Modal
            zIndex={200000}
            open={isVisible}
            onCancel={handleCancel}
            footer={null}
            maskClosable={false}
            closable={false}
        >
            <div className="modal-content">
                <div className="page-wrapper-new p-0">
                    <div className="content">
                        <div className="modal-header border-0 custom-modal-header">
                            <div className="page-title">
                                <h4>Bulk Upload</h4>
                                <p className="mb-0">Select a CSV or Excel file to upload</p>
                            </div>
                        </div>

                        <div className="modal-body custom-modal-body">
                            <div className='content'>
                                <div className='' style={{ fontStyle: "italic" }}>*Required field labels</div>
                                <p className='mt-1' style={{ textAlign: "justify" }}>
                                    The correct column order is (record_number, leave_days, epf_number, leave_type, appplied_on, status, reason)
                                </p>
                                <p className='mt-1' style={{ textAlign: "justify", color: "#dc3545" }}>
                                    <strong>Date Format:</strong> Use MM/DD/YYYY for leave_days (e.g., 06/28/2025) and DD/MM/YYYY for appplied_on (e.g., 15/06/2025)
                                </p>
                            </div>
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }} className="mt-3">
                                <div className="mb-3 input-blocks" style={{ flex: 1, marginRight: "20px" }}>
                                    <label className="form-label">File Upload (CSV or Excel)</label>
                                    <input
                                        type="file"
                                        className="form-control mt-0"
                                        onChange={handleBulkUpload}
                                        id='csvId'
                                        accept='.csv,.xlsx,.xls'
                                    />
                                </div>
                                <div className="mb-3 input-blocks">
                                    <label className="form-label pl-0">Sample File</label>
                                    <button className='btn pe-6 download-btn'
                                            onClick={downloadSampleFile}
                                    >Download <DownloadOutlined /></button>
                                </div>
                            </div>
                            {data && (
                                <div className="mt-2 mb-3">
                                    <small style={{ color: "#28a745" }}>
                                        ✓ File loaded successfully. {data.length} records found.
                                    </small>
                                </div>
                            )}
                            <div className="pt-3" style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}>
                                <button
                                    type="button"
                                    className="btn btn-cancel"
                                    onClick={handleCancel}
                                >
                                    Close
                                </button>
                                <div>
                                    <button
                                        type="button"
                                        className="btn btn-cancel me-2"
                                        onClick={handleReset}
                                    >
                                        Reset
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-submit ml-0"
                                        onClick={handleUpload}
                                        disabled={loading || !data}
                                    >
                                        {loading ? <Spin size="small" /> : "Upload"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

BulkUploadModal.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    handleCancel: PropTypes.func.isRequired,
    onUploadData: PropTypes.func.isRequired
};

export default BulkUploadModal;
